<script lang="ts" setup>
import { ButtonSize, ButtonStyle } from '~/static/enums/components/button'
import AButton from '~/components/atoms/buttons/Button.vue'
import MImageLoader from '~/components/molecules/forms/ImageLoader.vue'
import RModalShortsVideo from '~/components/organs/modals/ShortsVideo.vue'
import { openModal } from '~/utils/Modal'

const emit = defineEmits<{
  (e: 'image-selected', file: File): void
}>()

const changeImage = (file: File): void => {
  emit('image-selected', file)
}

const openVideo = (id: string) => {
  openModal(RModalShortsVideo, { id })
}
</script>

<template>
  <section class="shorts">
    <div class="container-lg">
      <div class="row justify-content-lg-center">
        <h2 class="shorts__title">
          Посмотрите, как сделать карточку с&nbsp;инфографикой за минуту
        </h2>
      </div>
    </div>
    <div class="shorts__previews">
      <a
        class="shorts__preview"
        target="_blank"
        rel="nofollow"
        href="#"
        @click.prevent="openVideo('winter-hat')"
      >
        <div class="btn-play"><BootstrapIconPlayFill /></div>
        <NuxtImg
          format="webp"
          src="/home/video/preview-winter-hat.webp"
          alt="Превью видео ВК “Инфографика для зимней шапки в Магви”"
          sizes="261px 991px:271px 1199px:261px 1399px:306px"
          fetchpriority="low"
          loading="lazy"
          densities="x1 x2"
          width="522"
          height="750"
          style="height: auto; aspect-ratio: 522 / 750;"
        />
      </a>
      <a
        class="shorts__preview"
        target="_blank"
        rel="nofollow"
        href="#"
        @click.prevent="openVideo('sneakers')"
      >
        <div class="btn-play"><BootstrapIconPlayFill /></div>
        <NuxtImg
          format="webp"
          src="/home/video/preview-sneakers.webp"
          alt="Превью видео ВК “Инфографика для кроссовок в Магви”"
          sizes="261px 991px:271px 1199px:261px 1399px:306px"
          fetchpriority="low"
          loading="lazy"
          densities="x1 x2"
          width="522"
          height="750"
          style="height: auto; aspect-ratio: 522 / 750;"
        />
      </a>
      <a
        class="shorts__preview"
        target="_blank"
        rel="nofollow"
        href="#"
        @click.prevent="openVideo('shampoo')"
      >
        <div class="btn-play"><BootstrapIconPlayFill /></div>
        <NuxtImg
          format="webp"
          src="/home/video/preview-shampoo.webp"
          alt="Превью видео ВК “Инфографика для подушки в Магви”"
          sizes="261px 991px:271px 1199px:261px 1399px:306px"
          fetchpriority="low"
          loading="lazy"
          densities="x1 x2"
          width="522"
          height="750"
          style="height: auto; aspect-ratio: 522 / 750;"
        />
      </a>
      <a
        class="shorts__preview"
        target="_blank"
        rel="nofollow"
        href="#"
        @click.prevent="openVideo('pillow')"
      >
        <div class="btn-play"><BootstrapIconPlayFill /></div>
        <NuxtImg
          format="webp"
          src="/home/video/preview-pillow.webp"
          alt="Превью видео ВК “Инфографика для шампуня в Магви”"
          sizes="261px 991px:271px 1199px:261px 1399px:306px"
          fetchpriority="low"
          loading="lazy"
          densities="x1 x2"
          width="522"
          height="750"
          style="height: auto; aspect-ratio: 522 / 750;"
        />
      </a>
    </div>
    <div class="container-lg">
      <div class="d-flex justify-content-lg-center">
        <MImageLoader
          v-slot="scope"
          @change="changeImage"
        >
          <AButton
            :button-style="ButtonStyle.Primary"
            @click="scope.loadImage"
            :button-size="ButtonSize.Large"
            class="shorts__btn"
          >
            Загрузить фото товара
          </AButton>
        </MImageLoader>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.shorts {
  padding: rem(70px) 0 rem(128px);
  display: flex;
  flex-direction: column;
  align-items: center;

  @include media-breakpoint-down(lg) {
    padding: rem(60px) 0 rem(128px);
  }

  @include media-breakpoint-down(md) {
    font-size: rem(80px);
  }

  @include media-breakpoint-down(sm) {
    font-size: rem(32px);
  }

  &__title {
    text-align: center;
    max-width: rem(859px);
    margin-bottom: rem(32px);
    font-size: rem(40px);

    @include media-breakpoint-down(xl) {
      font-size: rem(32px);
    }
    
    @include media-breakpoint-down(lg) {
      text-align: start;
      margin-bottom: rem(24px);
    }

    @include media-breakpoint-down(sm) {
      font-size: rem(24px);
    }
  }

  &__previews {
    display: grid;
    overflow: scroll;
    margin-bottom: rem(24px);
    max-width: 100%;
    padding: 0 rem(12px);
    grid-auto-flow: column;
    grid-template-columns: repeat(4, 1fr);
    gap: rem(24px);

    @include media-breakpoint-up(lg) {
      margin-bottom: rem(40px);
    }

    @include media-breakpoint-up(xl) {
      max-width: 1140px;
    }

    @include media-breakpoint-up(xxl) {
      max-width: 1320px;
    }

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__preview {
    min-width: rem(261px);
    transition: opacity .2s ease-in-out;
    position: relative;

    img {
      border-radius: rem(4px);
      max-width: 100%;
      width: 100%;
    }

    .btn-play {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: $indigo;
      border-radius: 50%;
      width: rem(56px);
      height: rem(56px);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 44px;
      color: $white;

      svg {
        width: 1em;
        height: 1em;
        margin-left: rem(6px);
      }
    }

    @media (any-hover: hover) {
      &:hover {
        opacity: 0.7;
      }
    }
  }

  &__btn {

    @include media-breakpoint-down(lg) {
      width: rem(351px);
      align-self: start;
    }

    @include media-breakpoint-down(sm) {
      width: 100%;
    }
  }
}
</style>
